import angular from 'angular'
import { ApiButtonLayout, ApiProduct } from '@vend/types'

import { $q } from '../ng-compat/imports'
import { EventEmitter } from '../util/event-emitter'

type Events = {
  layoutUpdate: [$TSFixMe]
  layoutUpdatingChange: [$TSFixMe]
  layoutUpdateError: [$TSFixMe]
}

/**
 * Class that provides a mechanism for interfacing between the vd-quick-keys directive and the consuming application
 * when editing a quick keys layout e.g. for adding products (to create quick keys) or showing product data related
 * to a quick key.
 *
 * @module vendQuickKeys.QuickKeysLayoutDataAdaptor
 */
angular
  .module('vendQuickKeys')
  .factory('QuickKeysLayoutDataAdaptor', function () {
    /**
     * Class that provides a mechanism for interfacing between the vd-quick-keys directive and the consuming application
     * adding products (to create quick keys).
     *
     * @class QuickKeysLayoutDataAdaptor
     * @extends EventEmitter
     * @constructor
     *
     * @param [config] {Object}
     *        Configuration object currently used to pass a product-info getter from the consuming application.
     *
     * 				@param [config.getProductFn] {Function}
     * 				       Method used to obtain additional information for a product associated with a quick key. Currently
     * 				       required for qk-modal where product name and sku are displayed.
     */
    class QuickKeysLayoutDataAdaptor extends EventEmitter<Events> {
      private _getProduct: $TSFixMe
      private _layout: $TSFixMe
      private _registered: $TSFixMe

      constructor(config: $TSFixMe = {}) {
        super()
        this._getProduct = config.getProductFn || function () {}
        this._registered = $q.defer()
      }

      // ============================== Public API ============================== //

      /**
       * A method that will resolve once the _registerQuickKeysLayout is run, used to check when the layout adaptor is
       * registered
       *
       * @method whenRegistered
       *
       * @return {Promise} A promise resolved when _registerQuickKeysLayout is run
       */
      public whenRegistered() {
        return this._registered.promise
      }

      /**
       * Add the given product to the quick keys layout. A quick key will be created at the current "edit target" location
       * with the associated product details.
       *
       * @method addProduct
       *
       * @param  {Product} product
       *         The product to add a quick key for
       *
       * @param  {Object} [options]
       *
       *         @param {Boolean} [options.exactMatch=false]
       *                Whether the product being added is an exact match for the current variant or whether it should be
       *                added as a generic variant parent (which will trigger the variant picker). Not applicable for
       *                products that do not have variants.
       *
       * @return {Promise} A promise resolved once the quick key has been added and the layout updated or rejected if
       *                   an error was encountered in the process
       */
      public addProduct(
        product: ApiProduct,
        options?: {
          exactMatch?: boolean
        }
      ) {
        return $q((resolve, reject) => {
          if (!this._layout) {
            return reject(
              new Error(
                'No layout has been registered with the edit adaptor yet'
              )
            )
          }

          if (!this._layout.editable) {
            return reject(
              new Error(
                'The layout registered with the edit adaptor is not currently editable'
              )
            )
          }

          this._layout.addQuickKey(product, options).then(resolve, reject)
        })
      }

      /**
       * Responsible for retrieving information for a product associated with a quick key. Employs the getProductFn method
       * provided by the consuming application.
       *
       * @method getProduct
       *
       * @param  {String} productId
       *         The product id
       *
       * @return {Object|Promise} The details for a product or a promise which resolves them
       */
      public getProduct(productId: string) {
        return this._getProduct(productId)
      }

      /**
       * Retrieve the current close folder option, if this is true, quick key folders will be closed once a quick key has
       * been selected
       *
       * @method getCloseFolderOption
       *
       * @return {Boolean} A boolean representing the current close folder option
       */
      public getCloseFolderOption() {
        return Boolean(this._layout && this._layout.getCloseFolderOption())
      }

      /**
       * Apply the given close folder setting to the quick key layout.
       *
       * @method setCloseFolderOption
       *
       * @param {Boolean} closeFolderOption
       *
       * @return {Object|Promise} A promise resolved once the quick key layout has been updated with the given close
       *                          folder option
       */
      public setCloseFolderOption(closeFolderOption: boolean) {
        return $q((resolve, reject) => {
          if (!this._layout) {
            return reject(
              new Error(
                'No layout has been registered with the edit adaptor yet'
              )
            )
          }

          this._layout
            .setCloseFolderOption(closeFolderOption)
            .then(resolve, reject)
        })
      }

      /**
       * Whether the layout has an update in progress.
       *
       * @property updateInProgress
       * @type {Boolean}
       * @readonly
       */
      public get updateInProgress() {
        return this._layout && this._layout.updateInProgress
      }

      // ============================== Protected API ============================== //

      /**
       * Register the given QuickKeysLayout instance with the adaptor.
       *
       * @protected
       * @method _registerQuickKeysLayout
       *
       * @param  {QuickKeysLayout} quickKeysLayout
       *         The QuickKeysLayout model instance to register with this adaptor.
       */
      protected _registerQuickKeysLayout(quickKeysLayout: ApiButtonLayout) {
        this._layout = quickKeysLayout

        this._layout.registerDataAdaptor(this)
        this._layout.on('layoutUpdatingChange', (event: $TSFixMe) =>
          this.emit('layoutUpdatingChange', event)
        )
        this._layout.on('layoutUpdate', (event: $TSFixMe) =>
          this.emit('layoutUpdate', event)
        )
        this._layout.on('layoutUpdateError', (event: $TSFixMe) =>
          this.emit('layoutUpdateError', event)
        )

        this._registered.resolve()
      }
    }

    return QuickKeysLayoutDataAdaptor
  })
